import { Trade, WalletRawTradeDto } from '@prediction-market/common';
import { PredictionType } from '@prisma/client';
import { PublicKey } from '@solana/web3.js';
import BN from 'bn.js';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { EOrder } from '@/components/pages/event/mutation/order-shared';
import {
  ORDER_OUTCOME,
  ORDER_TYPE,
  PRICE_DECIMALS,
  SIZE_DECIMALS,
} from '@/constants/solana';
import { Orderbook, OrderbookState } from '@/types/contract';

export interface PaginatedResult<T> {
  data: T[];
  meta: {
    page: number;
    perPage: number;
    totalCount: number;
    totalPages: number;
  };
}

export function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function formatFullDate(date: Date) {
  date = new Date(date);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(date)
    .replace(/\u202F/g, ' ');
}

export const formatDateToHHMM = (date: Date): string => {
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const shortenAddress = (address: string, fromStart = 4, fromEnd = 4) => {
  return `${address.slice(0, fromStart)}...${address.slice(-fromEnd)}`;
};

export const fromBN = (bn?: BN) => {
  return BigInt((bn ?? 0).toString());
};

export const toBN = (n: number | string | bigint) => {
  return new BN(n.toString());
};

export const bnToBuffer = (
  bn: BN | number,
  endian: 'le' | 'be',
  length: number,
) => {
  const bnObj = typeof bn === 'number' ? new BN(bn) : bn;

  // Check if toBuffer method exists (bn.js has it, Anchor BN might not)
  if (typeof bnObj.toBuffer === 'function') {
    return bnObj.toBuffer(endian, length);
  }

  // Fallback implementation for BN instances without toBuffer
  const buffer = Buffer.alloc(length);
  const hex = bnObj.toString(16);

  // Ensure we have even length hex string
  const paddedHex = hex.length % 2 === 0 ? hex : '0' + hex;

  // Convert hex to buffer
  const hexBuffer = Buffer.from(paddedHex, 'hex');

  if (endian === 'le') {
    // For little endian, copy bytes in reverse order
    for (let i = 0; i < Math.min(hexBuffer.length, length); i++) {
      buffer[i] = hexBuffer[hexBuffer.length - 1 - i];
    }
  } else {
    // For big endian, copy bytes normally from the right
    const offset = Math.max(0, length - hexBuffer.length);
    hexBuffer.copy(buffer, offset);
  }

  return buffer;
};

export interface OrdersByPrice {
  price: number;
  size: number;
  usd: number;
  orders: {
    price: number;
    size: number;
    user: PublicKey;
  }[];
}

const padWithZeros = (
  array: OrdersByPrice[],
  minLength: number,
  isBid: boolean,
  outcome: ORDER_OUTCOME,
) => {
  const padding = minLength - array.length;
  if (padding <= 0) return array;

  // Create dummy entries with zero values
  const dummyEntries = Array(padding)
    .fill(0)
    .map((_, index) => ({
      price: 0,
      size: 0,
      usd: 0,
      orders: [],
    }));

  if (outcome === ORDER_OUTCOME.YES) {
    return isBid ? [...array, ...dummyEntries] : [...dummyEntries, ...array];
  } else {
    return isBid ? [...dummyEntries, ...array] : [...array, ...dummyEntries];
  }
};

export const formatDateToDayMonthYear = (date: Date | string) => {
  const d = new Date(date);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${day}.${month}.${year}`;
};

export const mapOrderbook = (
  orderbook: OrderbookState | null | undefined,
  outcome: ORDER_OUTCOME,
  limit = 99999999,
) => {
  if (!orderbook)
    return {
      asks: padWithZeros([], 4, false, outcome),
      bids: padWithZeros([], 4, true, outcome),
      totalUsd: 0,
      orders: [],
    };
  let currentAsk = orderbook.askHead;

  const asks = {} as Record<string, OrdersByPrice>;

  while (currentAsk !== 65535 && Object.keys(asks).length < limit) {
    // Limit to 10 orders for readability
    const order = orderbook.asks[currentAsk];
    if (order.isFilled === 0 && +order.expiryTimestamp > Date.now() / 1000) {
      // Only show active orders
      const displayPrice =
        outcome === ORDER_OUTCOME.YES ? order.price : 100 - order.price;
      if (asks[displayPrice]) {
        asks[displayPrice].size += order.size / SIZE_DECIMALS;
        asks[displayPrice].usd +=
          (order.size / SIZE_DECIMALS / PRICE_DECIMALS) * displayPrice;
        asks[displayPrice].orders.push({
          price: displayPrice,
          size: order.size / SIZE_DECIMALS,
          user: order.user,
        });
      } else {
        asks[displayPrice] = {
          price: displayPrice,
          size: order.size / SIZE_DECIMALS,
          usd: (order.size / SIZE_DECIMALS / PRICE_DECIMALS) * displayPrice,
          orders: [
            {
              price: displayPrice,
              size: order.size / SIZE_DECIMALS,
              user: order.user,
            },
          ],
        };
      }
    }
    currentAsk = order.next;
  }

  let currentBid = orderbook.bidHead;

  const bids = {} as Record<string, OrdersByPrice>;

  while (currentBid !== 65535 && Object.keys(bids).length < limit) {
    // Limit to 10 orders for readability
    const order = orderbook.bids[currentBid];
    if (order.isFilled === 0 && +order.expiryTimestamp > Date.now() / 1000) {
      // Only show active orders
      const displayPrice =
        outcome === ORDER_OUTCOME.YES ? order.price : 100 - order.price;

      if (bids[displayPrice]) {
        bids[displayPrice].size += order.size / SIZE_DECIMALS;
        bids[displayPrice].usd +=
          (order.size / SIZE_DECIMALS / PRICE_DECIMALS) * displayPrice;
        bids[displayPrice].orders.push({
          price: displayPrice,
          size: order.size / SIZE_DECIMALS,
          user: order.user,
        });
      } else {
        bids[displayPrice] = {
          price: displayPrice,
          size: order.size / SIZE_DECIMALS,
          usd: (order.size / SIZE_DECIMALS / PRICE_DECIMALS) * displayPrice,
          orders: [
            {
              price: displayPrice,
              size: order.size / SIZE_DECIMALS,
              user: order.user,
            },
          ],
        };
      }
    }
    currentBid = order.next;
  }

  // Convert to arrays
  const asksArray = Object.entries(asks)
    .map(([price, order]) => ({
      ...order,
      price: Number(price),
    }))
    .sort((a, b) => b.price - a.price);

  const bidsArray = Object.entries(bids)
    .map(([price, order]) => ({
      ...order,
      price: Number(price),
    }))
    .sort((a, b) => b.price - a.price);

  // Pad arrays with zeros if length < 5

  // Ensure at least 5 items in each array
  const paddedAsks = padWithZeros(asksArray, 4, false, outcome);
  const paddedBids = padWithZeros(bidsArray, 4, true, outcome);
  const totalUsd =
    paddedAsks.reduce((acc, curr) => acc + curr.usd, 0) +
    paddedBids.reduce((acc, curr) => acc + curr.usd, 0);

  return {
    totalUsd: Number(totalUsd.toFixed(2)),
    asks: outcome === ORDER_OUTCOME.YES ? paddedAsks : paddedBids,
    bids: outcome === ORDER_OUTCOME.YES ? paddedBids : paddedAsks,
  };
};

// Define a type for market order fills that works for both buy and sell
export interface MarketOrderFill {
  ordersToFill: Array<{ price: number; size: number }>;
  averagePrice: string;
  totalShares: number;
  totalCost: number; // Total USDC amount
  nextBestPrice: number | null;
}

export const calculateMarketOrderFill = (
  orderbookData: ReturnType<typeof mapOrderbook>,
  amount: number | string,
  orderType: ORDER_TYPE,
): MarketOrderFill => {
  const orders: Array<{ price: number; size: number }> = [];
  const parsedAmount =
    typeof amount === 'string' ? parseFloat(amount) || 0 : amount;
  let remainingAmount = parsedAmount;
  let totalCost = 0;
  let sharesAccumulated = 0;
  let nextBestPrice: number | null = null;

  if (!orderbookData) {
    return {
      ordersToFill: [],
      averagePrice: '0',
      totalShares: 0,
      totalCost: 0,
      nextBestPrice: null,
    };
  }

  if (orderType === ORDER_TYPE.BUY) {
    // For a market buy, we look at the ask (sell) side of the orderbook
    // and fill orders from lowest price to highest
    if (orderbookData.asks.length > 0) {
      // Sort asks by price (lowest first)
      const sortedAsks = [...orderbookData.asks]
        .filter((ask) => ask.price > 0) // Filter out dummy entries
        .sort((a, b) => a.price - b.price);

      nextBestPrice = sortedAsks[0]?.price || null;

      for (const ask of sortedAsks) {
        // Skip if we've used all the USDC
        if (remainingAmount <= 0) break;

        // Calculate maximum number of shares we can buy with remaining USDC at this price level
        const priceInDollars = ask.price / 100;
        const maxSharesAtThisLevel = remainingAmount / priceInDollars;

        // Check if we can fill the entire order level
        if (maxSharesAtThisLevel >= ask.size) {
          // We can buy all shares at this level
          const cost = ask.size * priceInDollars;
          orders.push({ price: ask.price, size: ask.size });
          remainingAmount -= cost;
          totalCost += cost;
          sharesAccumulated += ask.size;
        } else {
          // We can only buy some shares at this level
          orders.push({ price: ask.price, size: maxSharesAtThisLevel });
          totalCost += maxSharesAtThisLevel * priceInDollars;
          sharesAccumulated += maxSharesAtThisLevel;
          remainingAmount = 0;
        }
      }
    }
  } else {
    // For a market sell, we look at the bid (buy) side of the orderbook
    // and fill orders from highest price to lowest
    if (orderbookData.bids.length > 0) {
      // Sort bids by price (highest first)
      const sortedBids = [...orderbookData.bids]
        .filter((bid) => bid.price > 0) // Filter out dummy entries
        .sort((a, b) => b.price - a.price);

      nextBestPrice = sortedBids[0]?.price || null;
      for (const bid of sortedBids) {
        // Skip if we've sold all our shares
        if (remainingAmount <= 0) break;

        // Calculate how many shares we can sell at this price level
        const sharesToSell = Math.min(remainingAmount, bid.size);

        // Calculate the USDC we'll receive
        const priceInDollars = bid.price / 100;
        const receivedAmount = sharesToSell * priceInDollars;

        orders.push({ price: bid.price, size: sharesToSell });
        remainingAmount -= sharesToSell;
        totalCost += receivedAmount;
        sharesAccumulated += sharesToSell;
      }
    }
  }

  // Calculate average price in cents
  const avgPrice =
    sharesAccumulated > 0 ? (totalCost / sharesAccumulated) * 100 : 0;

  return {
    ordersToFill: orders,
    averagePrice: avgPrice.toFixed(2),
    totalShares: sharesAccumulated,
    totalCost: totalCost,
    nextBestPrice,
  };
};

export const formatDecimalLeadingZeros = (
  num: number | string,
  decimals = 2,
  maxZeros = 8,
) => {
  if (!num) return '0.00';
  const str = num.toString();

  // For integers, just add .00
  if (!str.includes('.')) return `${str}.00`;

  // For numbers with decimals
  const [whole, decimal] = str.split('.');
  if (decimals === 0) return `${whole}`;

  // Count leading zeros
  let leadingZeros = 0;
  for (let i = 0; i < decimal.length; i++) {
    if (decimal[i] === '0') leadingZeros++;
    else break;
  }

  if (leadingZeros > maxZeros)
    return `${whole}.${decimal.substring(0, decimals)}`;

  // Format to 2 digits after leading zeros
  const significantDecimals = decimal.substring(leadingZeros);
  const formattedDecimal =
    significantDecimals.length > decimals
      ? significantDecimals.substring(0, decimals)
      : significantDecimals.padEnd(decimals, '0');

  return `${whole}.${decimal.substring(0, leadingZeros)}${formattedDecimal}`;
};

export enum OrderType {
  MARKET = 'market',
  LIMIT = 'limit',
  MARKETLIMIT = 'marketlimit',
}

export const getOrderType = (
  price: number,
  order: EOrder,
  size: number,
  lastPrice: number,
  orderBookData: Orderbook,
  type: ORDER_TYPE,
) => {
  if (order === EOrder.MARKET) {
    let orderBookPriceTotalSize = 0;
    for (const priceOrder of [...orderBookData.asks].sort((a, b) =>
      type === ORDER_TYPE.BUY ? a.price - b.price : b.price - a.price,
    )) {
      for (const order of priceOrder.orders) {
        const expected = orderBookPriceTotalSize + order.size;

        if (expected === size) {
          return {
            orderType: OrderType.MARKET,
            partial: false,
          };
        }
        orderBookPriceTotalSize = expected;
      }
    }

    return {
      orderType: OrderType.MARKET,
      partial: true,
    };
  }
  if (lastPrice === 0) {
    return { orderType: OrderType.LIMIT, partial: false };
  }
  if (type === ORDER_TYPE.BUY ? price < lastPrice : price > lastPrice) {
    return { orderType: OrderType.LIMIT, partial: false };
  }

  const orders =
    type === ORDER_TYPE.BUY ? orderBookData.asks : orderBookData.bids;

  const matchingOrders = orders.filter((a) =>
    type === ORDER_TYPE.BUY ? a.price <= price : a.price >= price,
  );
  const orderBookPriceTotalSize = matchingOrders.reduce(
    (acc, curr) => acc + curr.size,
    0,
  );

  if (orderBookPriceTotalSize >= size) {
    let filledSize = 0;
    for (const matchingPriceOrder of matchingOrders) {
      for (const order of matchingPriceOrder.orders) {
        const expected = filledSize + order.size;
        if (expected === size) {
          return {
            orderType: OrderType.MARKET,
            partial: false,
          };
        }
        filledSize = expected;
      }
    }
    return {
      orderType: OrderType.MARKET,
      partial: orderBookPriceTotalSize > size,
    };
  }

  return { orderType: OrderType.MARKETLIMIT, partial: false };
};

/**
 * Formats a number with appropriate suffixes (k, m, b, t)
 * @param value Number to format
 * @param decimals Number of decimal places (default: 1)
 * @returns Formatted string with suffix
 */
export const formatNumberWithSuffix = (value: number, decimals = 1): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  // Handle negative numbers
  const sign = value < 0 ? '-' : '';
  const absValue = Math.abs(value);

  // Define suffixes and thresholds
  const suffixes = ['', 'k', 'm', 'b', 't'];

  // Find the appropriate suffix
  const tier =
    Math.floor(Math.log10(absValue) / 3) > 0
      ? Math.floor(Math.log10(absValue) / 3)
      : 0;

  // Don't go beyond available suffixes
  if (tier >= suffixes.length) {
    return sign + absValue.toExponential(decimals);
  }

  // Calculate scaled value
  const scaled = absValue / Math.pow(10, tier * 3);

  // Format the number with appropriate decimals
  // If the scaled value is an integer, don't show decimals
  const formattedValue =
    Number.isInteger(scaled) && tier === 0
      ? scaled.toString()
      : scaled.toFixed(decimals);

  // Remove trailing zeros and decimal point if needed
  const trimmedValue = formattedValue.replace(/\.0+$/, '');

  return sign + trimmedValue + suffixes[tier];
};

// Example with dollar sign
export const formatCurrency = (
  value: number,
  decimals = 1,
  withSign = true,
): string => {
  return (withSign ? '$' : '') + formatNumberWithSuffix(value, decimals);
};

export const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());

  // Convert to seconds, minutes, hours, days
  const diffSeconds = Math.floor(diffTime / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) {
    return '<1m ago';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else {
    return `${diffDays}d ago`;
  }
};

export const getMatchingAmount = (
  orderbookData: ReturnType<typeof mapOrderbook>,
  type: ORDER_TYPE,
  price: number,
  size: number,
) => {
  // Get relevant side of the orderbook (asks for BUY, bids for SELL)
  let relevantOrders = orderbookData[
    type === ORDER_TYPE.BUY ? 'asks' : 'bids'
  ].filter((a) =>
    type === ORDER_TYPE.BUY ? a.price <= price : a.price >= price,
  );

  // Sort orders for optimal fill
  relevantOrders = relevantOrders.sort((a, b) =>
    type === ORDER_TYPE.BUY ? a.price - b.price : b.price - a.price,
  );

  // Trackers
  let remainingSize = size;
  let filledSize = 0;
  let totalCost = 0;

  // Fill orders
  for (let i = 0; i < relevantOrders.length; i++) {
    const order = relevantOrders[i];
    if (remainingSize <= 0) break;
    const sizeFromThisOrder = Math.min(order.size, remainingSize);
    filledSize += sizeFromThisOrder;
    totalCost += sizeFromThisOrder * order.price;
    remainingSize -= sizeFromThisOrder;
  }
  const matchingCost = totalCost;
  totalCost += remainingSize * price;

  // Find next best price logic
  let nextBestPrice: number | null = null;

  if (remainingSize > 0) {
    // Not all filled, check for next best price

    const allOrders = [
      ...orderbookData[type === ORDER_TYPE.BUY ? 'asks' : 'bids'],
    ]
      .filter(
        (a) =>
          a.price > 0 &&
          (type === ORDER_TYPE.BUY ? a.price > price : a.price < price),
      )
      .sort((a, b) =>
        type === ORDER_TYPE.BUY ? a.price - b.price : b.price - a.price,
      );

    if (allOrders.length > 0) {
      nextBestPrice = allOrders[0].price;
    } else {
      // No more orders, return user price + 1 (or -1 for SELL)
      nextBestPrice = type === ORDER_TYPE.BUY ? price + 1 : price - 1;
    }
  } else {
    const allOrders = [
      ...orderbookData[type === ORDER_TYPE.BUY ? 'asks' : 'bids'],
    ]
      .filter((a) => a.price > 0)
      .sort((a, b) =>
        type === ORDER_TYPE.BUY ? a.price - b.price : b.price - a.price,
      );

    // Fully filled at best price, it's a market order
    nextBestPrice = allOrders[0]?.price || null;
  }

  return {
    matchingAmount: filledSize,
    matchingCost: matchingCost,
    totalCostUsdc: parseFloat(totalCost.toFixed(6)),
    averagePrice:
      filledSize > 0 ? parseFloat((totalCost / filledSize).toFixed(6)) : 0,
    averageLimitPrice: totalCost / size,
    isFullyFilled: filledSize >= size,
    nextBestPrice,
  };
};

export const getLiquidityIncentiveFeeDiscount = (
  tradeFeePercent: number,
  referralFee: number,
  liquidityIncentiveDiscountPerSpread: number,
  liquidityIncentiveMaxSpread: number,
  price: number | string | null,
  nextBestPrice: number | null,
) => {
  const marketFee = tradeFeePercent - tradeFeePercent * referralFee;

  const baseFee = {
    marketFee,
    limitFee: marketFee,
    liquidityIncentiveFeeDiscount: 0,
    spread: 0,
  };

  if (!price) return baseFee;

  if (!nextBestPrice) return baseFee;

  const spread = Math.abs(Number(price) - nextBestPrice) || 1;

  if (spread > liquidityIncentiveMaxSpread) return baseFee;

  const liquidityIncentiveFeeDiscount =
    (liquidityIncentiveMaxSpread + 1 - spread) *
    liquidityIncentiveDiscountPerSpread;

  const tradeFeeWithLiquidityIncentive =
    tradeFeePercent - liquidityIncentiveFeeDiscount;

  return {
    limitFee:
      tradeFeeWithLiquidityIncentive -
      tradeFeeWithLiquidityIncentive * referralFee,
    marketFee,
    liquidityIncentiveFeeDiscount,
    spread,
  };
};

/**
 * Recursively formats two big numbers as strings with suffixes ("T", "B", "M", "K", etc)
 * until their formatted representations are different.
 *
 * @param a string - first big number as string
 * @param b string - second big number as string
 * @returns [string, string] - formatted representations of a and b
 */
export function formatBigNumbersUntilDifferent(
  a: string,
  b: string,
): [string, string] {
  const SUFFIXES = [
    { value: 1e12, suffix: 'T' },
    { value: 1e9, suffix: 'B' },
    { value: 1e6, suffix: 'M' },
    { value: 1e3, suffix: 'K' },
    { value: 1, suffix: '' },
  ];

  function format(num: number, divisor: number, suffix: string): string {
    const abs = Math.abs(num);
    let decimals = 0;
    const divided = abs / divisor;
    if (divisor >= 1e3) {
      if (divided >= 100) decimals = 0;
      else if (divided >= 10) decimals = 1;
      else if (divided >= 1) decimals = 2;
      // If divided < 1, we should use the next lower suffix
    }
    // For divisor < 1e3, always 0 decimals
    else {
      decimals = 0;
    }
    // If divided < 1, force 0 decimals (should trigger next suffix anyway)
    if (divided < 1) decimals = 0;
    return (num / divisor).toFixed(decimals) + suffix;
  }

  function recurse(aNum: number, bNum: number, idx: number): [string, string] {
    if (idx >= SUFFIXES.length) {
      // fallback: just return as is
      return [aNum.toString(), bNum.toString()];
    }
    const { value, suffix } = SUFFIXES[idx];
    const aFormatted = format(aNum, value, suffix);
    const bFormatted = format(bNum, value, suffix);

    // If either formatted value is less than 1 in this suffix, go to next lower suffix
    if (
      Math.abs(aNum) / value < 1 ||
      Math.abs(bNum) / value < 1 ||
      aFormatted === bFormatted
    ) {
      return recurse(aNum, bNum, idx + 1);
    }
    return [aFormatted, bFormatted];
  }

  const aNum = Number(a);
  const bNum = Number(b);

  return recurse(aNum, bNum, 0);
}

export const mapMarketCategory = (predictionType: PredictionType) => {
  if (predictionType === 'PRICE_ABOVE_BELOW') {
    return 'Price Target';
  }

  if (predictionType === 'END_WITH_NUMBER' || predictionType === 'EVEN_ODD') {
    return 'Roulette Bets';
  }

  if (
    predictionType === 'WALLET_BUY_SELL' ||
    predictionType === 'WALLET_PROFIT_LOSS'
  ) {
    return 'Wallet Moves';
  }

  if (predictionType === 'MCAP_BATTLE' || predictionType === 'VOLUME_BATTLE') {
    return 'Token Battles';
  }

  if (predictionType === 'COMBO') {
    return 'Combo Plays';
  }

  return '';
};

export const mapTrade = (trade: WalletRawTradeDto): Trade => {
  return {
    from: {
      address: trade.fromToken,
      amount: Number(trade.fromAmount || 0),
      priceUsd: Number(trade.fromPriceUsd || 0),
      token: {
        decimals: trade.fromDecimals || 9,
        name: trade.fromSymbol || '',
        symbol: trade.fromSymbol || '',
        image: trade.fromImage || '',
      },
    },
    to: {
      address: trade.toToken,
      amount: Number(trade.toAmount || 0),
      priceUsd: Number(trade.toPriceUsd || 0),
      token: {
        decimals: trade.toDecimals || 9,
        name: trade.toSymbol || '',
        symbol: trade.toSymbol || '',
        image: trade.toImage || '',
      },
    },
    time: new Date(trade.timestamp).getTime(),
    tx: trade.txHash,
    price: { usd: Number(trade.fromPriceUsd || 0), sol: '0' },
    volume: { usd: Number(trade.fromAmount || 0), sol: 0 },
    wallet: trade.fromToken,
    program: trade.fromToken,
  };
};

export function realized(trades: Trade[], tokenAddress?: string): number {
  if (!trades.length) return 0;

  if (!tokenAddress) {
    const byToken = new Map<string, Trade[]>();

    for (const t of trades) {
      const push = (addr: string) => {
        if (!byToken.has(addr)) byToken.set(addr, []);
        byToken.get(addr)!.push(t);
      };
      push(t.to.address);
      if (t.from.address !== t.to.address) push(t.from.address);
    }

    let total = 0;
    for (const [token, list] of byToken) {
      total += realized(list, token);
    }
    return total;
  }

  const ordered = trades
    .filter(
      (t) => t.to.address === tokenAddress || t.from.address === tokenAddress,
    )
    .sort((a, b) => a.time - b.time);

  let invQty = 0;
  let invCostUsd = 0;
  let pnl = 0;

  for (const t of ordered) {
    if (t.to.address === tokenAddress) {
      const qty = t.to.amount;
      invQty += qty;
      invCostUsd += qty * (t.to.priceUsd ?? 0);
      continue;
    }

    if (invQty === 0) continue;
    const sellQty = Math.min(t.from.amount, invQty);
    const proceeds = sellQty * (t.from.priceUsd ?? 0);
    const avgCost = invCostUsd / invQty;
    const costShare = avgCost * sellQty;

    pnl += proceeds - costShare;
    invQty -= sellQty;
    invCostUsd -= costShare;
  }

  return pnl;
}

export function formatNumberAbbreviated(num: number, digits = 2): string {
  if (num === null || num === undefined || isNaN(num)) return '0';
  const absNum = Math.abs(num);

  if (absNum < 1) return formatDecimalLeadingZeros(absNum, digits);
  const sign = num < 0 ? '-' : '';
  const units = [
    { value: 1e12, symbol: 'T' },
    { value: 1e9, symbol: 'B' },
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'k' },
  ];
  for (const unit of units) {
    if (absNum >= unit.value) {
      return (
        sign +
        (absNum / unit.value)
          .toFixed(digits)
          .replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1') +
        unit.symbol
      );
    }
  }
  return (
    sign + absNum.toFixed(digits).replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1')
  );
}

// Unified assert function that works like Node.js assert
export function assert<T>(
  condition: T | undefined | null | boolean,
  message?: string,
): asserts condition is T {
  if (condition === undefined || condition === null || condition === false) {
    throw new Error(message || 'Assertion failed');
  }
}

// Type guard for object properties
export function assertProperty<T, K extends keyof T>(
  obj: T,
  key: K,
  message?: string,
): asserts obj is T & Record<K, NonNullable<T[K]>> {
  if (obj[key] === undefined || obj[key] === null) {
    throw new Error(message || `Property ${String(key)} is required`);
  }
}

export const cleanMarketNameForUrl = (name: string, id: string | number) => {
  return `${
    name
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces/whitespace with hyphens
      .replace(/&/g, 'n') // Replace & with n (representing "and")
      .replace(/[%?#=+\/\\<>"'`{}|^~]/g, '') // Remove only problematic URL characters
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
  }-${id}`;
};
